import { format, parse } from "date-fns";
import {
  getWeekDay,
  removeTimezoneFromDateString,
} from "../../../utils/dateUtils";
import {
  calendarItemDoesNotRepeatOption,
  calendarItemRecurrence,
  lateCancelOptions,
  reservationsCloseOptions,
  reservationsOpenOptions,
} from "../Calendar.utils";
import { StaffProfileSelectFragment } from "./../../../components/controllers/StaffProfileSelect/StaffProfilesSelect-generated.hooks";
import { TaxSelectFragment } from "./../../../components/controllers/TaxSelect/TaxSelect-generated.hooks";
import {
  UpdateCalendarItemMutationVariables,
  PatchCalendarTemplateMutationVariables,
  UpdateCalendarTemplateMutationVariables,
  CreateCalendarTemplateMutationVariables,
  CreateCalendarItemMutationVariables,
  GetCalendarItemForEditQuery,
} from "./CalendarForm-generated.hooks";
import { LocationControllerOptionFragment } from "../../../components/controllers/LocationSelect/LocationSelect-generated.hooks";
import { CalendarItemTypeSelectFragment } from "../../../components/controllers/CalendarItemTypeSelect/CalendarItemTypeSelect-generated.hooks";

type classEnds = "never" | "on" | "after";

export interface ClassEndsForm {
  type: classEnds;
  date: string;
  number: number;
}

export interface CalendarForm {
  // Details
  name: string;
  date: {
    start: string;
    end?: string;
  };
  time: {
    start: string;
    end: string;
  };
  mainCoach?: StaffProfileSelectFragment;
  assistantCoach?: StaffProfileSelectFragment;
  // Settings
  reservationsOpen: {
    label: string;
    value: number;
  };
  reservationsClose: {
    label: string;
    value: number;
  };
  attendanceLimit: {
    hasAttendanceLimit: boolean;
    number: number;
  };
  location?: LocationControllerOptionFragment;
  description?: string;
}

export interface ClassForm extends CalendarForm {
  recurrence?: {
    label: string;
    value: number;
  };
  weekDays?: string[];
  classEnds?: ClassEndsForm;
  type: Omit<CalendarItemTypeSelectFragment, "isDeleted" | "isActive">;
  lateCancel: {
    label: string;
    value: number;
  };
}

export interface EventForm extends CalendarForm {
  price?: number;
  tax?: TaxSelectFragment;
  allDay?: boolean;
  inviteOnly?: boolean;
  showOnMemberApp?: boolean;
  redirectUrl?: string;
}

export const parseClassFormDefaultValues = (
  calendarItem: GetCalendarItemForEditQuery["getCalendarItem"],
  isDuplicate?: boolean,
): Partial<ClassForm> => {
  const startTimestamp = new Date(
    removeTimezoneFromDateString(calendarItem.startDatetime),
  ).getTime();
  const endTimestamp = new Date(
    removeTimezoneFromDateString(calendarItem.endDatetime),
  ).getTime();

  const calendarFormDefaultValues: ClassForm = {
    // Details
    name: isDuplicate
      ? `Duplicate of ${calendarItem.title}`
      : calendarItem.title,
    type: calendarItem.calendarItemType
      ? {
          name: calendarItem.calendarItemType.name,
          color: calendarItem.calendarItemType.color,
          id: calendarItem.calendarItemType.id,
          uuid: calendarItem.calendarItemType.uuid,
        }
      : undefined,
    date: {
      start: format(startTimestamp, "yyyy-MM-dd"),
      end: format(endTimestamp, "yyyy-MM-dd"),
    },
    time: {
      start: format(startTimestamp, "h:mm a"),
      end: format(endTimestamp, "h:mm a"),
    },
    mainCoach: calendarItem.mainCoach,
    assistantCoach: calendarItem.assistantCoach,
    // Settings
    reservationsOpen:
      reservationsOpenOptions.find(
        (option) => option.value === calendarItem.registrationStartOffset,
      ) || reservationsOpenOptions[0],
    reservationsClose:
      reservationsCloseOptions.find(
        (option) => option.value === calendarItem.registrationEndOffset,
      ) || reservationsCloseOptions[0],
    attendanceLimit: {
      hasAttendanceLimit: !!calendarItem.attendanceCap,
      number: calendarItem.attendanceCap || 0,
    },
    lateCancel:
      lateCancelOptions.find(
        (option) => option.value === calendarItem.registrationLateCancelOffset,
      ) || lateCancelOptions[0],
    // TODO It would be better if this returned a LOCATION type instead of a CALENDAR LOCATION TYPE
    location: calendarItem.location?.uuid
      ? {
          uuid: calendarItem.location.uuid,
          name: calendarItem.location.name,
          isActive: true,
          isDeleted: false,
        }
      : undefined,

    description: calendarItem.description,
  };

  if (isDuplicate) {
    // A calendarItem can have multiple templates since we can edit different weeksDays, making them individual templates
    const templates = calendarItem.templates?.filter(
      (template) => !template.deleted,
    );

    if (templates?.length === 0) {
      // if does not have template attached, it should just not repeat
      calendarFormDefaultValues.recurrence = calendarItemDoesNotRepeatOption;
    } else {
      const template = templates[0];

      // All the templates will have the same interval, since you can't edit a template interval on the edit mode
      const intervalOption = calendarItemRecurrence.find(
        (recurrence) => recurrence.value === template.interval,
      );
      calendarFormDefaultValues.recurrence = intervalOption;

      // Get days selection
      const days = templates?.map((temp) => temp.days).flat();
      calendarFormDefaultValues.weekDays = convertShortNamesToFullNames(days);

      // Get class ends selection
      calendarFormDefaultValues.classEnds = {
        type: template.endDate ? "on" : template?.count ? "after" : "never",
        number: template.count ?? 1,
        date: template.endDate ?? format(endTimestamp, "yyyy-MM-dd"),
      };
      calendarFormDefaultValues.date.end = template.endDate ?? undefined;
    }
  }

  return calendarFormDefaultValues;
};

export const parseEventFormDefaultValues = (
  calendarItem: GetCalendarItemForEditQuery["getCalendarItem"],
  isDuplicate?: boolean,
) => {
  const calendarFormDefaultValues: EventForm = {
    // Details
    name: isDuplicate
      ? `Duplicate of ${calendarItem.title}`
      : calendarItem.title,
    date: {
      start: format(
        removeTimezoneFromDateString(calendarItem.startDatetime),
        "yyyy-MM-dd",
      ),
      end: format(
        removeTimezoneFromDateString(calendarItem.endDatetime),
        "yyyy-MM-dd",
      ),
    },
    time: {
      start: format(
        removeTimezoneFromDateString(calendarItem.startDatetime),
        "h:mm a",
      ),
      end: format(
        removeTimezoneFromDateString(calendarItem.endDatetime),
        "h:mm a",
      ),
    },
    allDay: calendarItem.isAllDay,
    mainCoach: calendarItem.mainCoach,
    assistantCoach: calendarItem.assistantCoach,
    price: calendarItem.price || 0,
    tax: calendarItem.tax.uuid
      ? {
          uuid: calendarItem.tax.uuid,
          name: calendarItem.tax.name,
        }
      : undefined,

    // Settings
    reservationsOpen:
      reservationsOpenOptions.find(
        (option) => option.value === calendarItem.registrationStartOffset,
      ) || reservationsOpenOptions[0],
    reservationsClose:
      reservationsCloseOptions.find(
        (option) => option.value === calendarItem.registrationEndOffset,
      ) || reservationsCloseOptions[0],
    attendanceLimit: {
      hasAttendanceLimit: !!calendarItem.attendanceCap,
      number: calendarItem.attendanceCap || 0,
    },

    // TODO It would be better if this returned a LOCATION type instead of a CALENDAR LOCATION TYPE
    location: calendarItem.location?.uuid
      ? {
          uuid: calendarItem.location.uuid,
          name: calendarItem.location.name,
          isActive: true,
          isDeleted: false,
        }
      : undefined,

    description: calendarItem.description,
    showOnMemberApp: calendarItem.isPublic,
    redirectUrl: calendarItem.redirectUrl,
    inviteOnly: calendarItem.registrationAccess === "invite_only",
  };

  return calendarFormDefaultValues;
};

function convertDatesToCalendarTemplate(
  date: CalendarForm["date"],
  time: CalendarForm["time"],
  allDay?: EventForm["allDay"],
): {
  startDate: CreateCalendarItemMutationVariables["calendarItemInput"]["startDate"];
  endDate: CreateCalendarItemMutationVariables["calendarItemInput"]["endDate"];
  startTime: CreateCalendarItemMutationVariables["calendarItemInput"]["startTime"];
  endTime: CreateCalendarItemMutationVariables["calendarItemInput"]["endTime"];
  isAllDay: CreateCalendarItemMutationVariables["calendarItemInput"]["isAllDay"];
} {
  if (allDay) {
    return {
      startDate: date.start,
      endDate: date.end || date.start,
      isAllDay: true,
      startTime: undefined,
      endTime: undefined,
    };
  }

  const startTime24h = format(parse(time.start, "h:mm a", new Date()), "HH:mm");
  const endTime24h = format(parse(time.end, "h:mm a", new Date()), "HH:mm");

  return {
    startDate: date.start,
    endDate: date.end,
    isAllDay: allDay,
    startTime: startTime24h,
    endTime: endTime24h,
  };
}

export function parseEventFormToMutationInputValue(
  uuid: string,
  values: EventForm,
): UpdateCalendarItemMutationVariables["updateCalendarItemInput"] {
  const updateCalendarItemInput: UpdateCalendarItemMutationVariables["updateCalendarItemInput"] =
    {
      // updating calendar item uuid
      uuid,

      // details fields
      calendarTypeId: undefined,
      title: values.name,
      ...convertDatesToCalendarTemplate(
        values.date,
        values.time,
        !!values.allDay,
      ), // date fields
      price: values.price || 0,
      taxUuid: values.tax?.uuid,
      coachUuid: values.mainCoach?.userUuid || null,
      assistantCoachUuid: values.assistantCoach?.userUuid || null,

      // settings fields
      registrationStartOffset: values.reservationsOpen.value,
      registrationEndOffset: values.reservationsClose.value,
      registrationAccess: values.inviteOnly ? "invite_only" : "open",
      lateCancelOffset: 0,
      attendanceCap: values.attendanceLimit.hasAttendanceLimit
        ? values.attendanceLimit.number
        : 0,
      locationUuid: values.location?.uuid,
      description: values.description,
      isPublic: !!values.showOnMemberApp,
      redirectUrl: values.redirectUrl,

      // fields we dont update at UI
      type: "event",
      rrule: undefined,
    };

  return updateCalendarItemInput;
}

export function parseClassFormToMutationInputValue(
  uuid: string,
  values: ClassForm,
): UpdateCalendarItemMutationVariables["updateCalendarItemInput"] {
  const updateCalendarItemInput: UpdateCalendarItemMutationVariables["updateCalendarItemInput"] =
    {
      // updating calendar item uuid
      uuid,

      // details fields
      calendarTypeId: values.type.id,
      title: values.name,
      ...convertDatesToCalendarTemplate(values.date, values.time), // date fields
      coachUuid: values.mainCoach?.userUuid || null,
      assistantCoachUuid: values.assistantCoach?.userUuid || null,

      // settings fields
      registrationStartOffset: values.reservationsOpen.value,
      registrationEndOffset: values.reservationsClose.value,
      lateCancelOffset: values.lateCancel.value,
      attendanceCap: values.attendanceLimit.hasAttendanceLimit
        ? values.attendanceLimit.number
        : 0,
      locationUuid: values.location?.uuid || null,
      description: values.description,

      // fields we don't update on the UI
      price: undefined,
      taxUuid: undefined,
      rrule: undefined,
      isPublic: undefined,
      redirectUrl: undefined,
      type: "class",
    };

  return updateCalendarItemInput;
}

// Abbreviate the day name, example: Friday to fri
export function abbreviateDays(values: string[]) {
  return values?.reduce<string[]>((abbreviations, day) => {
    return [...abbreviations, day.slice(0, 2).toLowerCase()];
  }, []);
}

// Get the full day name from abbreviate day name, example: fri to Friday
export function convertShortNamesToFullNames(shortNames: string[]): string[] {
  const dayMapping: { [key: string]: string } = {
    mo: "Monday",
    tu: "Tuesday",
    we: "Wednesday",
    th: "Thursday",
    fr: "Friday",
    sa: "Saturday",
    su: "Sunday",
  };

  return shortNames.map(
    (shortName) => dayMapping[shortName.toLowerCase()] || shortName,
  );
}

export function parseCalendarFormToCreateClassTemplateMutationInputValue(
  values: ClassForm,
): CreateCalendarTemplateMutationVariables {
  // Format weekdays to be in the same format the backend expect
  const abbreviatedDays = abbreviateDays(
    (values.recurrence.value !== 0 && values.weekDays) || [
      getWeekDay(values.date.start),
    ],
  );

  let count: number;

  if (values.classEnds?.type === "after") {
    count = values.classEnds?.number;
  } else if (values.recurrence?.value === 0) {
    // If recurrence is 0 (does not have recurrence, just have 1 class)
    count = 1;
  }

  const createCalendarTemplateInput: CreateCalendarTemplateMutationVariables["createCalendarTemplateInput"] =
    {
      // details fields
      title: values.name,
      calendarTypeId: values.type.id,
      ...convertDatesToCalendarTemplate(values.date, values.time),
      interval: values.recurrence?.value || 1,
      count,
      days: abbreviatedDays || [],
      coachUuid: values.mainCoach?.userUuid,
      assistantCoachUuid: values.assistantCoach?.userUuid,

      // settings fields
      registrationStartOffset: values.reservationsOpen.value,
      registrationEndOffset: values.reservationsClose.value,
      lateCancelOffset: values.lateCancel.value,
      attendanceCap: values.attendanceLimit.hasAttendanceLimit
        ? values.attendanceLimit.number
        : 0,
      locationUuid: values.location?.uuid,
      description: values.description,
    };

  return { createCalendarTemplateInput };
}

export function parseCalendarFormToUpdateClassTemplateMutationInputValue({
  templateId,
  days,
  values,
}: {
  templateId: string;
  days?: string[];
  values: ClassForm;
}): UpdateCalendarTemplateMutationVariables["updateCalendarTemplateItemInput"] {
  // Format weekdays to be in the same format the backend expect
  const abbreviatedDays = days?.reduce<string[]>((abbreviations, day) => {
    return [...abbreviations, day.slice(0, 2).toLowerCase()];
  }, []);

  const variables: UpdateCalendarTemplateMutationVariables["updateCalendarTemplateItemInput"] =
    {
      // client fields
      series: templateId,
      ...convertDatesToCalendarTemplate(values.date, values.time),
      endDate: undefined, // Undefined cause I want update forever

      // details fields
      title: values.name,
      calendarTypeId: values.type.id,
      coachUuid: values.mainCoach?.userUuid || null,
      assistantCoachUuid: values.assistantCoach?.userUuid || null,
      days: abbreviatedDays,

      // settings fields
      registrationStartOffset: values.reservationsOpen.value,
      registrationEndOffset: values.reservationsClose.value,
      lateCancelOffset: values.lateCancel.value,
      attendanceCap: values.attendanceLimit.hasAttendanceLimit
        ? values.attendanceLimit.number
        : 0,
      locationUuid: values.location?.uuid || null,
      description: values.description,

      // fields that we don't have UI
      count: undefined,
      interval: undefined,
    };

  return variables;
}

export function parseCalendarFormToPatchClassTemplateMutationInputValue({
  templateId,
  days,
  values,
}: {
  templateId: string;
  days: string[];
  values: ClassForm;
}): PatchCalendarTemplateMutationVariables["patchCalendarTemplateItemInput"] {
  const updateVariables: UpdateCalendarTemplateMutationVariables["updateCalendarTemplateItemInput"] =
    parseCalendarFormToUpdateClassTemplateMutationInputValue({
      templateId,
      days,
      values,
    });

  const variables: PatchCalendarTemplateMutationVariables["patchCalendarTemplateItemInput"] =
    {
      ...updateVariables,
      days: abbreviateDays(updateVariables.days),
    };

  return variables;
}

export function parseCalendarFormToCreateEventMutationInputValue(
  values: EventForm,
): CreateCalendarItemMutationVariables {
  const calendarItemInput: CreateCalendarItemMutationVariables["calendarItemInput"] =
    {
      // client fields
      calendarTypeId: undefined,

      // details fields
      title: values.name,
      ...convertDatesToCalendarTemplate(
        values.date,
        values.time,
        values.allDay,
      ),
      coachUuid: values.mainCoach?.userUuid,
      assistantCoachUuid: values.assistantCoach?.userUuid,
      price: values.price || 0,
      taxUuid: values.tax?.uuid,
      rrule: undefined,

      // settings fields
      registrationStartOffset: values.reservationsOpen.value,
      registrationEndOffset: values.reservationsClose.value,
      lateCancelOffset: 0,
      attendanceCap: values.attendanceLimit.hasAttendanceLimit
        ? values.attendanceLimit.number
        : 0,
      locationUuid: values.location?.uuid,
      description: values.description,
      isPublic: !!values.showOnMemberApp,
      redirectUrl: values.redirectUrl,
      type: "event",
      registrationAccess: values.inviteOnly ? "invite_only" : "open",
    };

  return { calendarItemInput };
}

export function parseQuickEditChanges(
  uuid: string,
  type: string,
  values: Partial<
    UpdateCalendarItemMutationVariables["updateCalendarItemInput"]
  >,
): UpdateCalendarItemMutationVariables["updateCalendarItemInput"] {
  const result: UpdateCalendarItemMutationVariables["updateCalendarItemInput"] =
    {
      uuid,
      type,
      assistantCoachUuid: undefined,
      attendanceCap: undefined,
      calendarTypeId: undefined,
      coachUuid: undefined,
      description: undefined,
      endDate: undefined,
      endTime: undefined,
      isAllDay: undefined,
      isPublic: undefined,
      lateCancelOffset: undefined,
      locationUuid: undefined,
      price: undefined,
      redirectUrl: undefined,
      registrationEndOffset: undefined,
      registrationStartOffset: undefined,
      rrule: undefined,
      startDate: undefined,
      startTime: undefined,
      taxUuid: undefined,
      title: undefined,
      ...values,
    };

  return result;
}
